/* Verkleinerte Fassungen eines Fotos im Browser erzeugen — MIT Transparenz.
 *
 * Maße, Zuschnitt, Dateinamen und die Wahl des Formats stehen in
 * domain/photoVariants.js (dort getestet). Hier liegt nur der Teil, der einen
 * Browser braucht: Bild dekodieren, auf Canvas zeichnen, kodieren. In Node nicht
 * ausführbar.
 *
 * - Transparenz: Die Zeichenfläche bleibt durchsichtig. Früher wurde sie weiß
 *   gefüllt und als JPEG geschrieben (kein Alphakanal), dabei wurde jede
 *   transparente Fläche weiß. Jetzt wird WebP (mit Alpha) geschrieben, und wo der
 *   Browser kein WebP liefert (Safari), PNG; entscheidend ist der gelieferte
 *   Blob-Typ (encodeVariant). Nie eine Vollfarbe, nie JPEG.
 * - Drehung: Handyfotos liegen oft quer mit EXIF-Drehung. createImageBitmap mit
 *   imageOrientation 'from-image' dreht richtig. Kennt der Browser die Option
 *   nicht, wird über ein <img> dekodiert (das dreht von sich aus).
 * - Bei starkem Verkleinern in zwei Schritten (erst auf das Doppelte des Ziels),
 *   weil ein einzelner großer Sprung in manchen Browsern kantig aussieht.
 */

import {
  VARIANTS, VARIANT_NAMES, coverRect, outputSize, encodeVariant,
} from '../domain/photoVariants.js';

/* Auch für composables/shrinkLogo.js */
export async function decodeImage(blob) {
  try {
    const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
  } catch {
    const href = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.src = href;
      await img.decode();
      return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => {} };
    } finally {
      URL.revokeObjectURL(href);
    }
  }
}

export function newCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return { canvas, ctx };
}

/* canvas.toBlob liefert null bei Fehler; der Typ des Blobs ist der tatsächliche */
export function toBlobAs(canvas) {
  return (type, quality) =>
    new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

async function render(decoded, variant) {
  const crop = coverRect(decoded.width, decoded.height);
  const out = outputSize(crop, variant);

  let from = decoded.source;
  let fx = crop.sx, fy = crop.sy, fw = crop.sw, fh = crop.sh;

  if (crop.sw > out.width * 2) {
    const mid = newCanvas(out.width * 2, out.height * 2);
    mid.ctx.drawImage(from, fx, fy, fw, fh, 0, 0, mid.canvas.width, mid.canvas.height);
    from = mid.canvas;
    fx = 0; fy = 0; fw = mid.canvas.width; fh = mid.canvas.height;
  }

  /* Keine Füllung: Die neue Zeichenfläche ist vollständig transparent, das Foto wird
     darübergelegt, transparente Stellen des Originals bleiben transparent. */
  const { canvas, ctx } = newCanvas(out.width, out.height);
  ctx.drawImage(from, fx, fy, fw, fh, 0, 0, out.width, out.height);
  return encodeVariant(toBlobAs(canvas), VARIANTS[variant].quality);
}

/**
 * Fassungen erzeugen. names: welche (Vorgabe alle).
 * Liefert { thumb: { blob, ext, contentType }, md: { … } }; ext ist 'webp' oder 'png'
 * je nach dem, was der Browser liefert. Wirft, wenn das Bild nicht dekodiert werden kann.
 */
export async function makeVariants(blob, names = VARIANT_NAMES) {
  const decoded = await decodeImage(blob);
  try {
    const result = {};
    for (const name of names) result[name] = await render(decoded, name);
    return result;
  } finally {
    decoded.close();
  }
}
